import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiCheck, FiX, FiLoader } from 'react-icons/fi';
import './PaymentCallback.css';

// Single-origin in production builds and under the Vite dev proxy; the
// absolute URL only applies when the API is served elsewhere (VITE_API_URL).
const API_URL = import.meta.env.PROD
  ? '/api'
  : (import.meta.env.VITE_API_URL || 'http://localhost:5000/api');

const PaymentCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    // Tap can return different parameter names
    const tapId = query.get('tap_id') || query.get('id') || query.get('charge_id') || query.get('chargeId');
    const invoiceToken = query.get('token');
    
    console.log('Payment callback params:', { tapId, invoiceToken, allParams: Object.fromEntries(query) });
    
    if (tapId) {
      verifyPayment(tapId, invoiceToken);
    } else if (invoiceToken) {
      // If we have token but no tap_id, check invoice status directly
      checkInvoiceStatus(invoiceToken);
    } else {
      setStatus('error');
      setMessage('Invalid payment callback - missing parameters');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const checkInvoiceStatus = async (invoiceToken) => {
    try {
      const response = await fetch(`${API_URL}/public/invoice/${invoiceToken}`);
      const data = await response.json();
      
      if (data.invoice) {
        // Check if invoice was paid
        if (data.invoice.status === 'paid' || data.invoice.amount_due === 0) {
          setStatus('success');
          setMessage(`Payment completed successfully!`);
          setTimeout(() => {
            navigate(`/invoice/${invoiceToken}`);
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Payment was not completed. Please try again.');
        }
      } else {
        setStatus('error');
        setMessage('Could not verify payment status.');
      }
    } catch (error) {
      console.error('Invoice status check error:', error);
      setStatus('error');
      setMessage('Failed to verify payment. Please check your invoice.');
    }
  };

  const verifyPayment = async (tapId, invoiceToken) => {
    try {
      setStatus('processing');
      const response = await fetch(`${API_URL}/public/payment/verify?tap_id=${tapId}&token=${invoiceToken || ''}`);
      const data = await response.json();
      
      console.log('Payment verification response:', data);
      
      if (data.success && data.status === 'CAPTURED') {
        setStatus('success');
        setMessage(`Payment of ${data.amount} ${data.currency} completed successfully!`);
        
        // Redirect to invoice after 3 seconds
        if (invoiceToken) {
          setTimeout(() => {
            navigate(`/invoice/${invoiceToken}`);
          }, 3000);
        }
      } else if (data.status === 'INITIATED') {
        setStatus('pending');
        setMessage('Payment is being processed. You will be notified once completed.');
      } else {
        setStatus('error');
        setMessage(data.message || data.error || 'Payment was not completed');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('error');
      setMessage('Failed to verify payment status. Please check your invoice.');
    }
  };

  return (
    <div className="payment-callback">
      <div className="callback-card">
        {status === 'processing' && (
          <>
            <div className="callback-icon processing">
              <FiLoader className="spin" />
            </div>
            <h2>Processing Payment</h2>
            <p>Please wait while we verify your payment...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="callback-icon success">
              <FiCheck />
            </div>
            <h2>Payment Successful!</h2>
            <p>{message}</p>
            <p className="redirect-notice">Redirecting to invoice...</p>
          </>
        )}
        
        {status === 'pending' && (
          <>
            <div className="callback-icon pending">
              <FiLoader className="spin" />
            </div>
            <h2>Payment Processing</h2>
            <p>{message}</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="callback-icon error">
              <FiX />
            </div>
            <h2>Payment Not Completed</h2>
            <p>{message}</p>
            <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                Try Again
              </button>
              <button className="btn btn-primary" onClick={() => {
                const query = new URLSearchParams(location.search);
                const token = query.get('token');
                if (token) {
                  navigate(`/invoice/${token}`);
                }
              }}>
                View Invoice
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;